// The notifications list — loading, failed, empty and rows — shared by the
// desktop dropdown and the phone sheet so the two never drift.

import { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Bell, Info } from "lucide-react";

import { useGoogleConnect } from "@/api/google";
import type { NotificationItem, NotificationSeverity } from "@/api/notifications";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Time } from "@/components/ui/Time";
import { cx } from "@/components/ui/controlStyles";

interface NotificationListProps {
  items: NotificationItem[];
  loading: boolean;
  /** The request failed and nothing was read: say so, never "nothing here". */
  error?: boolean;
  onRetry?: () => void;
  onClose: () => void;
}

export function NotificationList({ items, loading, error, onRetry, onClose }: NotificationListProps) {
  if (loading && items.length === 0) {
    return (
      <div role="status" className="flex items-center justify-center gap-2 px-5 py-8 text-sm text-fg-muted">
        <Spinner className="h-4 w-4" /> Checking…
      </div>
    );
  }
  if (error && items.length === 0) {
    return (
      <div role="status" className="flex flex-col items-center gap-2 px-5 py-8 text-center">
        <p className="text-base font-medium text-fg">Can't check right now</p>
        {onRetry && (
          <Button variant="ghost" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
        <Bell aria-hidden className="h-5 w-5 text-fg-subtle" strokeWidth={1.75} />
        <p className="text-base text-fg-muted">No notifications</p>
      </div>
    );
  }
  return (
    <ul className="flex flex-col divide-y divide-line-subtle">
      {items.map((item) => (
        <NotificationRow key={item.id} item={item} onClose={onClose} />
      ))}
    </ul>
  );
}

// A body is clamped to four lines; "Show more" appears only when the clamp
// actually hides text (measured, so it tracks the panel's width).
function useClamped(expanded: boolean) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [clamped, setClamped] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded]);
  return { ref, clamped };
}

// Desktop pages the companion has under the same path (/companion + href).
const SAME_SHAPE = ["/subagents", "/tasks", "/schedules"];

/**
 * A subagent, task or schedule link (the bell's run alerts point at
 * /subagents/<t>/runs, its task items at /tasks/<id>, its schedule items
 * at /schedules?id=…) stays in the companion when the bell was opened
 * there; every other link is the desktop page it names, a tap away as
 * before.
 */
export function companionHref(href: string, pathname: string): string {
  const inCompanion = pathname === "/companion" || pathname.startsWith("/companion/");
  const same = SAME_SHAPE.some((p) => href === p || href.startsWith(p + "/") || href.startsWith(p + "?"));
  return inCompanion && same ? `/companion${href}` : href;
}

function NotificationRow({ item, onClose }: { item: NotificationItem; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const googleConnect = useGoogleConnect();
  const [expanded, setExpanded] = useState(false);
  // Only a Google reconnect runs asynchronously; each row has its own hook.
  const pending = item.action.type === "google_reconnect" && googleConnect.isPending;
  const body = useClamped(expanded);
  const long = expanded || body.clamped;

  async function onCta() {
    const action = item.action;
    if (action.type === "navigate") {
      onClose();
      navigate(companionHref(action.href, location.pathname));
      return;
    }
    if (action.type === "google_reconnect") {
      try {
        const { authorizeUrl } = await googleConnect.mutateAsync({
          customerId: action.customerId,
          template: action.template,
          displayName: action.displayName,
        });
        // Google's consent page opens in a new tab; this notification
        // clears itself once the fresh token lands (next poll, or the
        // callback's postMessage).
        window.open(authorizeUrl, "_blank", "noopener,noreferrer");
        onClose();
      } catch (e) {
        console.error("Google reconnect failed:", e);
      }
      return;
    }
    onClose();
  }

  const showCta = item.action.type !== "none" && item.ctaLabel;

  return (
    <li className="flex items-start gap-3 px-4 py-3.5">
      <SeverityIcon severity={item.severity} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 text-base font-medium text-fg [overflow-wrap:anywhere]">{item.title}</p>
          {item.at && <Time value={item.at} mode="relative" className="shrink-0 text-xs text-fg-subtle" />}
        </div>
        <p ref={body.ref} className={cx("mt-1 whitespace-pre-line text-sm text-fg-muted [overflow-wrap:anywhere]", !expanded && "line-clamp-4")}>
          {item.body}
        </p>
        {(showCta || long) && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {showCta && (
              <Button variant="secondary" size="sm" onClick={onCta} loading={pending}>
                {pending ? "Opening Google…" : item.ctaLabel}
                {!pending && <ArrowRight aria-hidden className="h-3.5 w-3.5" strokeWidth={1.75} />}
              </Button>
            )}
            {long && (
              <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
                {expanded ? "Show less" : "Show more"}
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

const SEVERITY: Record<NotificationSeverity, { label: string; className: string }> = {
  urgent: { label: "Urgent", className: "text-danger" },
  warning: { label: "Warning", className: "text-warning" },
  info: { label: "Information", className: "text-info" },
};

function SeverityIcon({ severity }: { severity: NotificationSeverity }) {
  const s = SEVERITY[severity] ?? SEVERITY.info;
  const Icon = severity === "info" ? Info : AlertTriangle;
  return (
    <span className="mt-0.5 flex shrink-0" title={s.label}>
      <Icon aria-hidden className={cx("h-4 w-4", s.className)} strokeWidth={1.75} />
      <span className="sr-only">{s.label}: </span>
    </span>
  );
}
